import type {
  IFilterOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IExecution } from "../types";
import { unformatBreakBuild, unformatKind, unformatStrictness } from "../utils";
import type { ForcesExecutionsInput } from "gql/graphql";
import { useFiltersStorage } from "storage/filters";
import { getCheckedFilter, getNewFilter } from "utils/filters";

const useDevSecOpsFilters = ({
  setFilters,
}: {
  setFilters: React.Dispatch<React.SetStateAction<ForcesExecutionsInput>>;
}): IUseFilterProps<IExecution> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const filterOptions: IOptionsProps<IExecution>[] = useMemo(
    (): IOptionsProps<IExecution>[] => [
      {
        filterOptions: [
          {
            key: "status",
            label: t("group.forces.status.title"),
            options: [
              { label: "Safe", value: "Safe" },
              { label: "Vulnerable", value: "Vulnerable" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.forces.status.title"),
      },
      {
        filterOptions: [
          {
            key: "strictness",
            label: t("group.forces.strictness.title"),
            options: [
              { label: "Strict", value: "Strict" },
              { label: "Tolerant", value: "Tolerant" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.forces.strictness.title"),
      },
      {
        filterOptions: [
          {
            key: "kind",
            label: t("group.forces.kind.title"),
            options: [
              { label: "Dynamic", value: "DYNAMIC" },
              { label: "Static", value: "STATIC" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.forces.kind.title"),
      },
      {
        filterOptions: [
          {
            key: "breakBuild",
            label: t("group.forces.exitCode.title"),
            options: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.forces.exitCode.title"),
      },
      {
        filterOptions: [
          {
            key: "gitRepo",
            label: `${t("group.forces.gitRepo")} is`,
            type: "text",
          },
          {
            filterFn: "includesInsensitive",
            key: "gitRepo",
            label: `or ${t("group.forces.gitRepo")} contains`,
            type: "text",
          },
        ],
        label: t("group.forces.gitRepo"),
      },
      {
        filterOptions: [
          {
            key: "date",
            label: t("group.forces.date"),
            type: "dateRange",
          },
        ],
        label: t("group.forces.date"),
      },
    ],
    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("forces") as string | null;
  }, [getValues]);

  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("forces", value);
    },
    [setValues],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IExecution>[]): void => {
      const filters = newFilters as IFilterOptions<IExecution>[];
      const status = getCheckedFilter(filters, "status")?.value;
      const strictness = getCheckedFilter(filters, "strictness")?.value;
      const kind = getCheckedFilter(filters, "kind")?.value;
      const breakBuild = getCheckedFilter(filters, "breakBuild")?.value;
      const gitRepoFilters = filters.filter(
        (filter): boolean => filter.key === "gitRepo",
      );
      const gitRepo = gitRepoFilters.find(
        (filter): boolean => filter.filterFn === "includesInsensitive",
      )?.value;
      const gitRepoExactFilter = gitRepoFilters.find(
        (filter): boolean => filter.filterFn !== "includesInsensitive",
      )?.value;
      const date = getNewFilter(filters, "date");
      const minValue =
        _.isEmpty(date?.minValue) && _.isNil(date?.minValue)
          ? undefined
          : String(date?.minValue);
      const maxValue =
        _.isEmpty(date?.maxValue) && _.isNil(date?.maxValue)
          ? undefined
          : String(date?.maxValue);

      setFilters({
        build:
          breakBuild === undefined ? undefined : unformatBreakBuild(breakBuild),
        fromDate: minValue,
        gitRepo: gitRepo === "" ? undefined : gitRepo,
        gitRepoExactFilter:
          gitRepoExactFilter === "" ? undefined : gitRepoExactFilter,
        status,
        strictness:
          strictness === undefined ? undefined : unformatStrictness(strictness),
        toDate: maxValue,
        type: kind === undefined ? undefined : unformatKind(kind),
      });
    },
    [setFilters],
  );

  return useFilters({
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options: filterOptions,
    setValues: handleSetValues,
  });
};

export { useDevSecOpsFilters };
